import { PlusInSquareIcon } from "assets/icons";
import { GET_LIST_REQUEST_DEFAULT_LIMIT } from "constants/common";
import { useBoolean } from "hooks";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Pagination, SearchInput } from "ui";

import {
  DeleteVideoModal,
  VideoModal,
  VideosTable,
  WatchVideoModal
} from "../../components";
import { ContentVideoListItem } from "../../content.types";
import { useVideosList } from "../../hooks";

import styles from "./Videos.module.scss";

const Videos = () => {
  const { t } = useTranslation();

  const {
    videosList,
    isLoading,
    page,
    searchValue,
    totalItems,
    sorting,
    setSorting,
    setSearchValue,
    setPage,
    refetchVideos
  } = useVideosList();
  const {
    value: isVideoModalOpen,
    setTrue: onOpenModal,
    setFalse: onCloseModal
  } = useBoolean(false);

  const [deletingVideoId, setDeletingVideoId] = useState<string | null>(null);
  const [editableVideo, setEditableVideo] =
    useState<ContentVideoListItem | null>(null);

  const handleCloseModalClick = () => {
    onCloseModal();
    setEditableVideo(null);
  };

  useEffect(() => {
    if (editableVideo) {
      onOpenModal();
    } else {
      onCloseModal();
    }
  }, [editableVideo, onOpenModal, onCloseModal]);

  const {
    value: isWatchVideoModalOpen,
    setTrue: onOpenWatchModal,
    setFalse: onCloseWatchModal
  } = useBoolean(false);
  const [watchedVideo, setWatchedVideo] = useState<ContentVideoListItem | null>(
    null
  );
  const handleOpenWatchModalClick = (video: ContentVideoListItem) => {
    onOpenWatchModal();
    setWatchedVideo(video);
  };
  const handleCloseWatchModalClick = () => {
    onCloseWatchModal();
    setWatchedVideo(null);
  };

  return (
    <div className={styles.videos}>
      <div className={styles.videos_headerControls}>
        <SearchInput
          value={searchValue}
          onChange={setSearchValue}
          placeholder={t("Search")}
        />

        <Button
          title={t("Add Video")}
          icon={<PlusInSquareIcon />}
          onClick={onOpenModal}
        />
      </div>

      <div className={styles.videos_tableContainer}>
        <VideosTable
          videos={videosList ?? []}
          isLoading={isLoading}
          sorting={sorting}
          setSorting={setSorting}
          refetchVideos={refetchVideos}
          onDeleteVideo={setDeletingVideoId}
          onEditVideo={setEditableVideo}
          onWatchVideo={handleOpenWatchModalClick}
        />
      </div>

      <Pagination
        currentPage={page}
        onChange={setPage}
        perPage={GET_LIST_REQUEST_DEFAULT_LIMIT}
        totalItems={totalItems}
      />

      <VideoModal
        editableVideo={editableVideo}
        isOpen={isVideoModalOpen}
        onClose={handleCloseModalClick}
        refetchVideos={refetchVideos}
      />

      <DeleteVideoModal
        id={String(deletingVideoId)}
        isOpen={Boolean(deletingVideoId)}
        onDeleteSuccess={refetchVideos}
        onClose={() => setDeletingVideoId(null)}
      />

      <WatchVideoModal
        isOpen={isWatchVideoModalOpen}
        video={watchedVideo}
        onClose={handleCloseWatchModalClick}
      />
    </div>
  );
};

export default Videos;
